'use client'

import type { IInquiryDocumentUpload } from '@repo/sdk'

import { Spinner } from '@repo/ui'
import { XIcon } from 'lucide-react'
import React, { useEffect } from 'react'

export type IOnAsyncFileUpload = (
  acceptedFile: IInquiryDocumentUpload,
  callback?: (docId?: number) => void,
) => void

interface IProps {
  docsState: [
    IInquiryDocumentUpload[],
    React.Dispatch<React.SetStateAction<IInquiryDocumentUpload[]>>,
  ]
  onUpload: IOnAsyncFileUpload
}

export const AsyncFilesList = ({ docsState, onUpload }: IProps) => {
  const [docs, setDocs] = docsState

  const onRemove = (index: number) => {
    setDocs(prev => prev.filter((doc, i) => i !== index))
  }

  const setDocStatus = (
    doc: IInquiryDocumentUpload,
    status: IInquiryDocumentUpload['status'],
    id?: number,
  ) => {
    setDocs(prevDocs =>
      prevDocs.map(
        prevDoc =>
          prevDoc.file === doc.file
            ? {
                ...prevDoc, // Spread the previous doc
                id, // Update the id to the new id
                status, // Update the status to 'uploading'
              }
            : prevDoc, // Return the original document if not matched
      ),
    )
  }

  if (docs.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      {docs.map((doc, index) => (
        <AsyncFileItem
          doc={doc}
          key={index}
          onRemove={onRemove.bind(null, index)}
          onUpload={onUpload}
          setDocStatus={setDocStatus}
        />
      ))}
    </div>
  )
}

export const AsyncFileItem = ({
  doc,
  onRemove,
  onUpload,
  setDocStatus,
}: {
  doc: IInquiryDocumentUpload
  onRemove: () => void
  onUpload: IOnAsyncFileUpload
  setDocStatus: (
    doc: IInquiryDocumentUpload,
    status: IInquiryDocumentUpload['status'],
    id?: number,
  ) => void
}) => {
  useEffect(() => {
    if (!doc.status) {
      setDocStatus(doc, 'pending')
      onUpload(doc, docId => {
        setDocStatus(doc, 'done', docId)
      })
    }
  }, [])

  return (
    <div className={'flex items-center justify-between'}>
      <p className={'whitespace-nowrap text-sm font-semibold'}>{doc.file.name}</p>
      <div className={'flex items-center gap-5'}>
        {doc.status === 'pending' ? (
          <Spinner className="font-sm" />
        ) : (
          <XIcon cursor={'pointer'} onClick={onRemove} size={14} />
        )}
      </div>
    </div>
  )
}
